import {SelectionModel} from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {defaultIfEmpty, lastValueFrom, Observable} from 'rxjs';
import {PageOffering} from "@core/model/side-nav.model";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {PaginatorService} from '@shared-services/paginator.service';
import {UtilsService} from "@shared-services/utils.service";
import {
  AssignedSuppliersDialogComponent
} from "../../../settings/payments/assigned-suppliers-dialog/assigned-suppliers-dialog.component";
import {PaymentConfigsService} from "../../../settings/service/payment-configs.service";
import {RequestPosDialogComponent} from "../../../support/request-pos-dialog/request-pos-dialog.component";
import {NotifyPosRequest, PosRequest} from "../../../support/request-pos-dialog/request-pos.model";
import {RequestPosService} from "../../../support/request-pos-dialog/request-pos.service";
import {
  AgreementType,
  buildTransactionSourceConfigString,
  CardReader,
  LeaseStatus,
  LeaseSummary,
  mapRequestsToData,
  mapRequestToData,
  OccupancyType,
  PaymentConfigData,
  PaymentConfigRequest,
  SupplierConfigSummary,
  SupplierSourceConfig,
  TransactionSource,
  TransactionSourceConfig
} from "../../leasing.model";
import {LeaseViewService} from "../lease-view.service";
import {
  PosSoftwareSettingsDialogComponent
} from './pos-software-settings-dialog/pos-software-settings-dialog.component';
import {ConsoleContextService} from "@security/console-context.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  BILLING_DATE_MISSING_BUTTON_LABEL,
  BILLING_DATE_MISSING_MESSAGE,
  isValidBillingDayOfMonth
} from "@features/leasing/pos-billing-date.util";

@Component({
  selector: 'app-lease-pos-settings',
  templateUrl: './lease-pos-settings.component.html',
  styleUrls: ['./lease-pos-settings.component.scss']
})
export class LeasePosSettingsComponent implements OnInit, AfterViewInit {
  @Input() id!: string;
  @Input() offering!: PageOffering;
  @Input() reload!: Observable<void>;

  @ViewChild('posSettingsPaginator') set posSettingsPaginator(paginator: MatPaginator) {
    if (paginator) paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    if (this.leaseTransactionSourceConfigDataSource) this.leaseTransactionSourceConfigDataSource.paginator = paginator;
  }
  @ViewChild('posSupplierPaginator') set posSupplierPaginator(paginator: MatPaginator) {
    if (paginator) paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    if (this.leaseSupplierSourceConfigDataSource) this.leaseSupplierSourceConfigDataSource.paginator = paginator;
  }

  @Output() reloadLease = new EventEmitter<void>();

  leaseSummary!: LeaseSummary;
  parentPaymentConfigs: PaymentConfigRequest[] = [];
  offeringPaymentConfigs: PaymentConfigRequest[] = []

  selectedPaymentConfig!: PaymentConfigData;

  cardReadersOriginal: CardReader[] = [];
  cardReaders: CardReader[] = [];
  availableCardReaders: { code: string, readers: CardReader[] }[] = [];
  newAvailableCardReaders: CardReader[] = [];
  newCardReaders: CardReader[] = [];

  leaseTransactionSourceConfigDataSource: MatTableDataSource<TransactionSourceConfig> = new MatTableDataSource<TransactionSourceConfig>([]);
  leaseSupplierSourceConfigDataSource: MatTableDataSource<SupplierConfigSummary> = new MatTableDataSource<SupplierConfigSummary>([]);

  paymentDeviceDataSource: MatTableDataSource<CardReader> = new MatTableDataSource<CardReader>([]);

  posSettingsDisplayedColumns = ['select', 'uuid', 'sourceType', 'provider', 'domainPrefix', 'startDate', 'endDate', 'activationStatus', 'action'];
  posSuppliersDisplayedColumns = ['select', 'displayName', 'uuid', 'supplierName', 'startDate', 'endDate'];

  posTransactionSourceSelection = new SelectionModel<TransactionSourceConfig>(false, []);
  posSupplierSourceSelection = new SelectionModel<SupplierConfigSummary>(false, []);

  selectedPosSettingDomainPrefix: string | null = null;
  selectedPosSupplierUuid: string | null = null;

  paymentConfigs: PaymentConfigData[] = [];

  isInternalUser = false;

  posDialogRef!: MatDialogRef<PosSoftwareSettingsDialogComponent>;

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private leaseViewService: LeaseViewService,
              public currentContext: ConsoleContextService,
              private offeringPaymentConfigService: PaymentConfigsService,
              private matDialog: MatDialog,
              private requestPosService: RequestPosService,
              public loader: LoadingService,
              public paginatorService: PaginatorService,
              private changeDetectorRefs: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.loadLease();
    this.isInternalUser = this.currentContext.checkUserFeatures(['leasing_internal_admin'], this.offering.offeringUuid);
    this.loadAssignedLeaseCardReaders();
    this.reload.subscribe(() => this.loadLease());
  }

  initHistory() {
    lastValueFrom(this.leaseViewService.getLeasePaymentConfigHistory(this.id))
      .then((res: PaymentConfigRequest[]) => {
        this.paymentConfigs = mapRequestsToData(res);
        this.loadLeasePaymentConfigHistoryToTable(this.paymentConfigs);
      });
  }

  loadLease() {
    this.leaseViewService.getOne(``, this.id).subscribe({
      next: value => {
        this.leaseSummary = value;
        this.loadAssignedLeaseCardReaders();
        this.loadParentConfigs();
      },
      error: err => console.log(err)
    })
  }

  ngAfterViewInit(): void {
  }

  updatePaymentConfigStatus(paymentConfigUuid: string, request: PosRequest) {
    this.leaseViewService.updatePaymentConfigStatus(paymentConfigUuid, this.id, request).subscribe({
      next: () => {
        this.loadParentConfigs();
        this.reloadLease.emit();
      },
      error: err => console.log(err)
    })
  }

  loadParentConfigs() {
    if (this.isSubLease) {
      this.loadLeaseParentPaymentConfig();
      return;
    }

    if (this.leaseSummary.occupancyType == OccupancyType.SINGLE) {
      this.loadOfferingParentPaymentConfigs();
      return;
    }

    this.initHistory();
  }

  loadLeaseParentPaymentConfig() {
    this.leaseViewService.getLeasePaymentConfigHistory(this.leaseSummary.parentUuid!)
      .pipe(defaultIfEmpty([]))
      .subscribe({
      next: value => {
        this.parentPaymentConfigs = value ? value! : [];
        this.initHistory();
      },
      error: err => {
        console.log('Parent lease does not have active payment config');
        console.log(err);
      }
    })
  }

  loadOfferingParentPaymentConfigs() {
    this.offeringPaymentConfigService.getDistrictPosConfigs().subscribe({
      next: value => {
        this.offeringPaymentConfigs = value;
        this.initHistory();
      },
      error: err => {
        console.log(err);
      }
    })
  }

  // --- CS-1369: Lightspeed plan renewal date -----------------------------------------------

  /**
   * Active single-lease VEND configs on this lease that have no plan renewal date captured.
   * Poster and the supplier-based configs are out of scope.
   */
  get configsMissingBillingDayOfMonth(): PaymentConfigData[] {
    return (this.paymentConfigs ?? []).filter(config =>
      config.transactionSource == TransactionSource.VEND
      && config.activationStatus == 'ACTIVATED'
      && !isValidBillingDayOfMonth((config.transactionSourceConfig as TransactionSourceConfig)?.billingDayOfMonth));
  }

  /** Internal admins only — nobody else can act on the prompt. */
  get showBillingDateMissingPanel(): boolean {
    return this.isInternalUser && this.configsMissingBillingDayOfMonth.length > 0;
  }

  getBillingDateMissingMessage(): string {
    return BILLING_DATE_MISSING_MESSAGE;
  }

  /** The plan renewal day on the currently selected config, if it has one. */
  get selectedConfigBillingDayOfMonth(): number | null {
    const config = this.selectedPaymentConfig;
    if (!config || config.transactionSource != TransactionSource.VEND) {
      return null;
    }
    const day = (config.transactionSourceConfig as TransactionSourceConfig)?.billingDayOfMonth;
    return isValidBillingDayOfMonth(day) ? Number(day) : null;
  }

  /** The prompt's "POS setup" button opens the setup dialog on the config that is missing a date. */
  openPosSetupForMissingBillingDate(): void {
    const config = this.configsMissingBillingDayOfMonth[0];
    if (!config) {
      return;
    }
    this.selectedPaymentConfig = config;
    this.posTransactionSourceSelection.clear();
    this.posTransactionSourceSelection.select(config.transactionSourceConfig as TransactionSourceConfig);
    this.selectedPosSupplierUuid = null;
    this.posSupplierSourceSelection.clear();
    this.editPosSettings();
  }

  protected readonly BILLING_DATE_MISSING_BUTTON_LABEL = BILLING_DATE_MISSING_BUTTON_LABEL;

  getActivationRequestMessage(): string {
    if (!this.posTransactionSourceSelection.isEmpty()) {
      return `The point-of-sale account ${this.selectedPaymentConfig!.activationStatus == 'ACTIVATION_REQUESTED' ? '' : 'cancellation'} has been requested and pending ${this.selectedPaymentConfig.activationStatus == 'ACTIVATION_REQUESTED' ? 'activation' : 'deactivation'}`;
    }
    return '';
  }

  loadAssignedLeaseCardReaders() {
    this.leaseViewService.getAssignedLeaseCardReaders(this.id).subscribe({
      next: value => {
        this.cardReadersOriginal = value.map(m => {
          return {...m};
        });
        this.cardReadersOriginal.forEach(ea => {
          ea.startDate = this.dateUtils.displayShortDate(ea.startDate);
          ea.endDate = this.dateUtils.displayShortDate(ea.endDate);
        })

        this.cardReaders = [...value];
        this.paymentDeviceDataSource = new MatTableDataSource<CardReader>(this.cardReaders);
        this.changeDetectorRefs.detectChanges();
        this.cardReaders.forEach(ea => {
          ea.startDate = this.dateUtils.displayShortDate(ea.startDate);
          ea.endDate = this.dateUtils.displayShortDate(ea.endDate);
        })
        this.cardReaders.forEach(ea => this.loadAvailableLeaseCardReaders(ea, false));

        this.setNewCardReaders();
      },
      error: err => console.log(err)
    })
  }

  get hasNoPricingPeriod(): boolean {
    if (this.leaseSummary) {
      return (this.leaseSummary.rentalType === null || this.leaseSummary.monthlyPricePerSqm === null)
    }
    return true;
  }

  get leaseIsNotApproved(): boolean {
    const unapprovedStatuses = [LeaseStatus.PLANNED, LeaseStatus.INVALID];
    if (this.leaseSummary) {
      return (unapprovedStatuses.includes(this.leaseSummary.leaseStatus))
    }
    return true;
  }

  loadAvailableLeaseCardReaders(reader: CardReader, newReader: boolean) {
    reader.startDate = this.dateUtils.displayShortDate(reader.startDate);
    reader.endDate = this.dateUtils.displayShortDate(reader.endDate);
    this.leaseViewService.getAvailableLeaseCardReaders(this.id, this.dateUtils.displayShortDate(reader.startDate)!, this.dateUtils.displayShortDate(reader.endDate)!).subscribe({
      next: value => {
        if (!newReader) {
          const objIndex = this.availableCardReaders.findIndex(f => f.code === reader.cardReaderCode!);
          if (objIndex < 0) {
            this.availableCardReaders.push({code: reader.cardReaderCode ? reader.cardReaderCode : 'new', readers: value});
          } else {
            this.availableCardReaders[objIndex].readers = value;
          }
        } else {
          this.newAvailableCardReaders = value;
        }
      },
      error: err => console.log(err)
    })
  }

  assignLeaseCardReaders(reader: CardReader) {
    reader.startDate = this.dateUtils.displayShortDate(reader.startDate);
    reader.endDate = this.dateUtils.displayShortDate(reader.endDate);
    reader.retailerId = this.leaseSummary.retailerId;
    reader.districtUuid = this.leaseSummary.districtUuid!;
    this.leaseViewService.assignLeaseCardReader(this.id, reader).subscribe({
      next: value => {
        this.loadLease();
        this.reloadLease.emit();
      },
      error: err => console.log(err)
    })

  }

  saveCardReadersChanges() {
    const cardReadersChanged = JSON.stringify(this.cardReadersOriginal) != JSON.stringify(this.cardReaders);

    if (cardReadersChanged) {
      this.cardReaders.forEach(reader => {
        this.assignLeaseCardReaders(reader);
      })
    }

    this.newCardReaders.forEach(ea =>  {
      if (ea.cardReaderCode != null) this.assignLeaseCardReaders(ea);
    })
  }

  cancelCardReaderChanges() {
    this.cardReaders = [...this.cardReadersOriginal];
  }

  get isLeaseActiveOrApproved(): boolean {
    if (this.leaseSummary) {
      return (this.leaseSummary.leaseStatus.toLowerCase() === LeaseStatus.ACTIVE.toLowerCase() ||
        this.leaseSummary.leaseStatus.toLowerCase() === LeaseStatus.APPROVED.toLowerCase());
    }
    return false;
  }

  setNewCardReaders() {
    this.newCardReaders = [];
    const extraCardReaders = 3 - this.cardReaders.length;
    if (this.leaseSummary) {
      for (let i = 0; i < extraCardReaders; i++) {
        this.newCardReaders.push(new CardReader({
          leaseUuid: this.leaseSummary.uuid,
          retailerId: this.leaseSummary.retailerId,
          districtUuid: this.leaseSummary.districtUuid,
          startDate: this.leaseSummary.leaseStartDate,
          endDate: this.leaseSummary.leaseEndDate
        }))
      }
    }

    this.newCardReaders.forEach(ea => this.loadAvailableLeaseCardReaders(ea, true));
  }

  requestPOS(cancel: boolean) {
    this.matDialog.open(RequestPosDialogComponent, {
      panelClass: 'dialog-large',
      data: {
        cancel: cancel,
        header: cancel ? 'Cancel a POS account' : 'Request a POS account',
        property: this.currentContext.getCurrentLocation(),
        offering: this.offering,
        retailerId: this.leaseSummary.retailerId,
        companyName: this.leaseSummary.retailerCompanyName,
        leaseId: this.leaseSummary.uuid,
        // CS-1369 — lets the cancellation screen derive the billable dates and access-until date.
        billingDayOfMonth: cancel ? this.selectedConfigBillingDayOfMonth : null
      }
    }).afterClosed().subscribe((res: { save: boolean, leaseId: string, request: PosRequest }) => {
      if (res.save) {
        const configId = cancel ? this.selectedPaymentConfig!.uuid! : this.utils.generateUuid();
        this.updatePaymentConfigStatus(configId, res.request);
      }
    });
  }

  getAvailableCardReaders(reader: CardReader): CardReader[] {
    if (this.availableCardReaders.length > 0) {
      const objIndex = this.availableCardReaders.findIndex(f => f.code === reader.cardReaderCode!);
      return this.availableCardReaders[objIndex] ? this.availableCardReaders[objIndex].readers : [];
    }
    return [];
  }

  get editingCardReaders(): boolean {
    const newCardReaders = this.newCardReaders.filter(f => f.cardReaderCode != null);
    return JSON.stringify(this.cardReadersOriginal) != JSON.stringify(this.cardReaders) || newCardReaders.length > 0;
  }

  get canRequestPos(): boolean {
    return this.isLeaseActiveOrApproved;
  }

  get canCancelPos(): boolean {
    if (!this.posTransactionSourceSelection.isEmpty() && this.selectedPaymentConfig) {
      const activationStatus = this.selectedPaymentConfig!.activationStatus;
      return activationStatus == 'ACTIVATED';
    }
    return false;
  }

  activatePOSAccount(active: boolean) {
    let paymentConfig = {...this.selectedPaymentConfig};
    if (paymentConfig.transactionSource == TransactionSource.NETCASH) {
      const status = active ? 'ACTIVATED' : 'DEACTIVATED'
      let activationRequest: PosRequest = new PosRequest(
        this.id,
        null,
        null,
        null,
        null,
        status
      );
      this.updatePaymentConfigStatus(paymentConfig.uuid!, activationRequest);
      return;
    }

    if ((paymentConfig.transactionSource == TransactionSource.VEND || paymentConfig.transactionSource == TransactionSource.POSTER) && paymentConfig.transactionSourceConfig) {
      if (active) {
        const request = new NotifyPosRequest(
          this.id,
            (paymentConfig.transactionSourceConfig as TransactionSourceConfig).loginUrl!,
            (paymentConfig.transactionSourceConfig as TransactionSourceConfig).loginUsername!,
          (paymentConfig.transactionSourceConfig as TransactionSourceConfig).loginPassword!,
        )
        let activationRequest: PosRequest = new PosRequest(
          this.id,
          null,
          null,
          null,
          null,
          'ACTIVATED'
        );
        this.requestPosService.notifyPosActivation(this.leaseSummary.retailerId, request).subscribe({
          next: () => this.updatePaymentConfigStatus(paymentConfig.uuid!, activationRequest),
          error: err => console.log(err)
        })
      }

      if (!active && (paymentConfig.transactionSource == TransactionSource.VEND || paymentConfig.transactionSource == TransactionSource.POSTER) && paymentConfig.transactionSourceConfig) {
        const request = new NotifyPosRequest(
          this.id,
          (this.selectedPaymentConfig.transactionSourceConfig as TransactionSourceConfig).loginUrl!
        )
        let activationRequest: PosRequest = new PosRequest(
          this.id,
          null,
          null,
          null,
          null,
          'DEACTIVATED'
        );
        this.requestPosService.notifyPosDeactivation(this.leaseSummary.retailerId, request).subscribe({
          next: () => this.updatePaymentConfigStatus(paymentConfig.uuid!, activationRequest),
          error: err => console.log(err)
        })
      }
    }
  }

  getActivationConfirmedMessage(): string {
    if (!this.posTransactionSourceSelection.isEmpty()) {
      return `The point-of-sale account has been ${this.selectedPaymentConfig!.activationStatus == 'ACTIVATED' ? 'activated' : 'deactivated'}. The retailer has been notified via email.`;
    }
    return '';
  }

  setSelectedPosSettings(row: TransactionSourceConfig): void {
    this.selectedPosSettingDomainPrefix = row.domainPrefix;
    this.selectedPaymentConfig = this.getLeasePaymentConfigByUuid(row.paymentConfigUuid!)!;
    this.posTransactionSourceSelection.toggle(row);

    if (this.posTransactionSourceSelection.selected.length == 0) this.selectedPosSettingDomainPrefix = null;

    this.selectedPosSupplierUuid = null;
    this.posSupplierSourceSelection.clear();
  }

  setSelectedPosSupplier(row: SupplierConfigSummary): void {
    this.selectedPosSupplierUuid = row.uuid;
    this.selectedPaymentConfig = this.paymentConfigs.filter(f => f.uuid == row.uuid)[0];
    this.posSupplierSourceSelection.toggle(row);

    if (this.posSupplierSourceSelection.selected.length == 0) this.selectedPosSupplierUuid = null;

    this.selectedPosSettingDomainPrefix = null;
    this.posTransactionSourceSelection.clear();
  }

  addNewPOSSettings(): void {
    const defaultType = this.isInternalUser ? this.allowedPosTypes[0] : this.allowedPosTypes[this.allowedPosTypes.length - 1];
    this.posDialogRef = this.matDialog.open(PosSoftwareSettingsDialogComponent, {
      panelClass: 'dialog-large',
      maxHeight: 'auto',
      data: {
        singleLeaseConfig: null,
        leaseStartDate: this.leaseSummary.leaseStartDate,
        leaseEndDate: this.leaseSummary.leaseEndDate,
        supplierConfig: null,
        offering: this.offering,
        allConfigs: this.paymentConfigs,
        allParentConfigs: this.parentPaymentConfigs,
        posType: defaultType,
        isEditing: false,
        allowedPosTypes: this.allowedPosTypes,
        parentComponent: this,
        offeringConfig: false
      }
    });

    this.posDialogRef.afterClosed().subscribe((response: {
      save: boolean,
      singleLeaseConfig: TransactionSourceConfig,
      supplierConfig: SupplierSourceConfig
    }) => {
      if (response.save) {

        const paymentConfigToSave: PaymentConfigData = new PaymentConfigData();
        paymentConfigToSave.leaseUuid = this.id;
        paymentConfigToSave.active = true;

        if (this.isSubLease) {
          paymentConfigToSave.transactionSource = TransactionSource.VEND_SUPPLIER_LEASE;
          paymentConfigToSave.transactionSourceConfig = response.supplierConfig;
          paymentConfigToSave.startDate = response.supplierConfig!.startDate;
          paymentConfigToSave.endDate = response.supplierConfig!.endDate;
        }

        if (this.isHeadLease) {
          paymentConfigToSave.transactionSource = response.singleLeaseConfig!.paymentSourceName;
          paymentConfigToSave.transactionSourceConfig = response.singleLeaseConfig;
          paymentConfigToSave.startDate = response.singleLeaseConfig!.startDate;
          paymentConfigToSave.endDate = response.singleLeaseConfig!.endDate;
        }

        if (this.leaseSummary.occupancyType == OccupancyType.SINGLE) {
          if (response.singleLeaseConfig) {
            paymentConfigToSave.transactionSource = response.singleLeaseConfig!.paymentSourceName;
            paymentConfigToSave.transactionSourceConfig = response.singleLeaseConfig;
            paymentConfigToSave.startDate = response.singleLeaseConfig!.startDate;
            paymentConfigToSave.endDate = response.singleLeaseConfig!.endDate;
          }

          if (response.supplierConfig && response.supplierConfig.paymentConfigUuid) {
            paymentConfigToSave.transactionSource = TransactionSource.VEND_SUPPLIER_OFFERING;
            paymentConfigToSave.transactionSourceConfig = response.supplierConfig;
            paymentConfigToSave.startDate = response.supplierConfig!.startDate;
            paymentConfigToSave.endDate = response.supplierConfig!.endDate;
          }
        }

        this.leaseViewService.savePaymentConfig(this.generateConfigRequest(paymentConfigToSave), this.leaseSummary.uuid)
          .subscribe({
            next: () => {
              this.reloadLease.emit();
              this.loadLeasePaymentConfigHistory();
            },
            error: err => console.log(err)
          })
      }
    });
  }

  editPosSettings(): void {
    let posType: string | null;
    if (this.selectedPaymentConfig.transactionSource === TransactionSource.VEND || TransactionSource.POSTER || TransactionSource.NETCASH) {
      posType = this.selectedPaymentConfig.transactionSource ? this.selectedPaymentConfig.transactionSource : null;
    } else {
      let posSupplier: PaymentConfigData = this.paymentConfigs.filter(f => f.uuid == this.selectedPosSupplierUuid)[0];
      posType = (posSupplier && posSupplier.transactionSource) ? posSupplier.transactionSource : null;
    }

    let supplierConfig: PaymentConfigData | null = this.getSupplierConfigFromSelection();
    let parentConfig: PaymentConfigData | null = null;
    if (supplierConfig) {
      parentConfig = this.getLeasePaymentConfigParentByUuid((supplierConfig!.transactionSourceConfig as SupplierSourceConfig).paymentConfigUuid!);
      (supplierConfig.transactionSourceConfig as SupplierSourceConfig).startDate = supplierConfig.startDate;
      (supplierConfig.transactionSourceConfig as SupplierSourceConfig).endDate = supplierConfig.endDate;
      (supplierConfig.transactionSourceConfig as SupplierSourceConfig).earliestTransactionDate = supplierConfig.earliestTransactionDate;
      (supplierConfig.transactionSourceConfig as SupplierSourceConfig).latestTransactionDate = supplierConfig.latestTransactionDate;
    }


    this.posDialogRef = this.matDialog.open(PosSoftwareSettingsDialogComponent, {
      panelClass: 'dialog-large',
      maxHeight: 'auto',
      data: {
        singleLeaseConfig: this.posTransactionSourceSelection.selected[0],
        supplierConfig: supplierConfig ? supplierConfig.transactionSourceConfig : null,
        leaseStartDate: this.leaseSummary.leaseStartDate,
        leaseEndDate: this.leaseSummary.leaseEndDate,
        offering: this.offering,
        posType: posType,
        allowedPosTypes: [posType],
        allConfigs: this.paymentConfigs,
        allParentConfigs: this.parentPaymentConfigs,
        isEditing: true,
        parentComponent: this,
        offeringConfig: false,
        activationStatus: this.selectedPaymentConfig.activationStatus
      }
    })

    this.posDialogRef.afterClosed().subscribe((response: {
      save: boolean,
      singleLeaseConfig: TransactionSourceConfig,
      supplierConfig: SupplierSourceConfig
    }) => {
      if (response.save) {
        let configToSave: PaymentConfigData | null = null;
        if (response.singleLeaseConfig.paymentSourceName == TransactionSource.VEND || response.singleLeaseConfig.paymentSourceName == TransactionSource.POSTER) {
          configToSave = this.getLeasePaymentConfigByUuid(this.selectedPaymentConfig.uuid!);
          configToSave!.transactionSourceConfig = response.singleLeaseConfig;
          configToSave!.startDate = response.singleLeaseConfig.startDate!;
          configToSave!.endDate = response.singleLeaseConfig.endDate!;
        }
        if (response.singleLeaseConfig.paymentSourceName != TransactionSource.VEND && response.singleLeaseConfig.paymentSourceName != TransactionSource.POSTER) {
          configToSave = supplierConfig;
          configToSave!.transactionSourceConfig = response.supplierConfig;
          configToSave!.startDate = response.supplierConfig.startDate!;
          configToSave!.endDate = response.supplierConfig.endDate!;
        }

        this.leaseViewService.savePaymentConfig(this.generateConfigRequest(configToSave!), this.leaseSummary.uuid)
          .subscribe({
            next: () => {
              this.reloadLease.emit();
              this.loadLeasePaymentConfigHistory();
            },
            error: err => console.log(err)
          })
      }
    });
  }

  getLeasePaymentConfigByDomainPrefix(domainPrefix: string): PaymentConfigData | null {
    let index: number = this.paymentConfigs.findIndex((val) =>
      (val.transactionSource == TransactionSource.VEND || val.transactionSource == TransactionSource.POSTER) && val.transactionSourceConfig
      && (val.transactionSourceConfig as TransactionSourceConfig)?.domainPrefix == domainPrefix);
    if (index >= 0) {
      return this.paymentConfigs.at(index)!;
    } else {
      return null;
    }
  }


  generateTransactionSourceConfigString(transactionSource: string | null, requestConfig: {}): string {
    // Behaviour unchanged — the denylist now lives beside mapRequestToData in leasing.model.ts so
    // the serialize/parse round trip is unit testable and shared with lease-details.
    return buildTransactionSourceConfigString(transactionSource, requestConfig);
  }

  generateConfigRequest(paymentConfig: PaymentConfigData): PaymentConfigRequest {
    const requestConfig = { ...paymentConfig.transactionSourceConfig };

    return {
      uuid: paymentConfig.uuid,
      leaseUuid: paymentConfig.leaseUuid,
      activationStatus: paymentConfig.activationStatus,
      billingUpdateRequested: paymentConfig.billingUpdateRequested,
      transactionSource: paymentConfig.transactionSource,
      transactionSourceConfig: this.generateTransactionSourceConfigString(paymentConfig.transactionSource, requestConfig),
      active: paymentConfig.active,
      startDate: paymentConfig.startDate,
      endDate: paymentConfig.endDate,
      earliestTransactionDate: paymentConfig.earliestTransactionDate,
      latestTransactionDate: paymentConfig.latestTransactionDate,
      minStartDate: paymentConfig.minStartDate,
      maxEndDate: paymentConfig.maxEndDate
    }
  }

  loadPosSuppliers(parentConfigUuid: string) {
    this.leaseViewService.getLeaseSuppliers(this.leaseSummary.parentUuid!, parentConfigUuid).subscribe({
      next: value => {
        this.posDialogRef.componentInstance.availableSuppliers = value.unassignedSuppliers;
        this.posDialogRef.componentInstance.availableSuppliers.push(...this.posDialogRef.componentInstance.assignedSuppliers);
      },
      error: err => console.log(err)
    })
  }

  loadOfferingSuppliers(parentConfigUuid: string) {
    this.offeringPaymentConfigService.checkSuppliers(parentConfigUuid).subscribe({
      next: value => {
        this.posDialogRef.componentInstance.availableSuppliers = value.unassignedSuppliers;
        this.posDialogRef.componentInstance.availableSuppliers.push(...this.posDialogRef.componentInstance.assignedSuppliers);
      },
      error: err => console.log(err)
    })
  }

  notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
  }

  cardReaderFutureDated(reader: CardReader) {
    return new Date(reader.startDate!) > new Date();
  }

  canDeleteSelectedPos(): boolean {
    return !(this.selectedPaymentConfig && (this.selectedPaymentConfig.earliestTransactionDate || this.selectedPaymentConfig.latestTransactionDate));
  }

  getSupplierConfigFromSelection(): PaymentConfigData | null {
    if (this.selectedPosSupplierUuid) {
      return this.paymentConfigs.filter(f => f.uuid == this.selectedPosSupplierUuid)[0];
    }
    return null;
  }

  checkSuppliers(row: TransactionSourceConfig) {
    this.matDialog.open(AssignedSuppliersDialogComponent, {
      panelClass: 'dialog-large',
      height: 'auto',
      data: {
        configUuid: row.paymentConfigUuid,
        offering: this.offering,
        configType: 'LEASE',
        leaseUuid: this.leaseSummary.uuid
      }
    })
  }

  private loadLeasePaymentConfigHistoryToTable(paymentData: PaymentConfigData[]): void {
    this.leaseTransactionSourceConfigDataSource.data = [];
    const singleLeaseData = paymentData.filter(f => f.transactionSource == TransactionSource.VEND || f.transactionSource == TransactionSource.POSTER || f.transactionSource == TransactionSource.NETCASH);
    const leaseSupplierData = paymentData.filter(f => f.transactionSource == TransactionSource.VEND_SUPPLIER_LEASE);
    const offeringSupplierData = paymentData.filter(f => f.transactionSource == TransactionSource.VEND_SUPPLIER_OFFERING);

    if (singleLeaseData.length > 0) {
      let singleLeaseConfigs = singleLeaseData.map(item => {
        if (item.transactionSourceConfig) {
          item.transactionSourceConfig!.paymentConfigUuid = item.uuid!;
          (item.transactionSourceConfig! as TransactionSourceConfig).activationStatus = item.activationStatus!;
          item.transactionSourceConfig!.startDate = item.startDate;
          item.transactionSourceConfig!.endDate = item.endDate;
          item.transactionSourceConfig!.latestTransactionDate = item.latestTransactionDate!;
          item.transactionSourceConfig!.earliestTransactionDate = item.earliestTransactionDate!;
        }

        return item.transactionSourceConfig as TransactionSourceConfig;
      });

      this.leaseTransactionSourceConfigDataSource = new MatTableDataSource<TransactionSourceConfig>(
        singleLeaseConfigs
      );
    }


    let supplierConfigs: SupplierConfigSummary[] = [];

    if (leaseSupplierData.length > 0) {
      leaseSupplierData.forEach(data => {
        if (data.transactionSourceConfig) {
          let transactionSourceConfig: SupplierSourceConfig = data.transactionSourceConfig as SupplierSourceConfig;
          let paymentConfigRequest: PaymentConfigRequest = this.parentPaymentConfigs.filter(fl => fl.uuid == transactionSourceConfig.paymentConfigUuid)[0];
          let parentConfig: PaymentConfigData | null = null;
          if (paymentConfigRequest) {
            parentConfig = mapRequestToData(paymentConfigRequest);
          }
          if (parentConfig) {
            const configs = transactionSourceConfig.suppliers!.map(m => {
              const config: SupplierConfigSummary = {
                uuid: data.uuid,
                domainPrefix: (parentConfig!.transactionSourceConfig! as TransactionSourceConfig).domainPrefix,
                supplierName: m.name,
                startDate: data.startDate,
                endDate: data.endDate,
                earliestTransactionDate: data.earliestTransactionDate,
                latestTransactionDate: data.earliestTransactionDate,
              }
              return config;
            })
            supplierConfigs.push(...configs);
          }
        }
      })
    }

    if (offeringSupplierData.length > 0) {
      let parentConfig: PaymentConfigData | null = null;
      offeringSupplierData.forEach(data => {
        const offeringParentConfig = this.offeringPaymentConfigs
          .filter(f => f.uuid == data.transactionSourceConfig?.paymentConfigUuid)[0];
        parentConfig = offeringParentConfig ? mapRequestToData(offeringParentConfig) : null;
        if (parentConfig && data.transactionSourceConfig) {
          const configs = (data.transactionSourceConfig as SupplierSourceConfig).suppliers!.map(m => {
            const config: SupplierConfigSummary = {
              uuid: data.uuid,
              domainPrefix: (parentConfig!.transactionSourceConfig as TransactionSourceConfig).domainPrefix,
              supplierName: m.name,
              startDate: data.startDate,
              endDate: data.endDate,
              earliestTransactionDate: data.earliestTransactionDate,
              latestTransactionDate: data.earliestTransactionDate,
            }
            return config;
          })
          supplierConfigs.push(...configs);
        }
      })
    }

    if (supplierConfigs.length > 0) {
      supplierConfigs = supplierConfigs.filter(f => f.supplierName != null);
      this.leaseSupplierSourceConfigDataSource = new MatTableDataSource<SupplierConfigSummary>(
        supplierConfigs
      );
    }

    this.changeDetectorRefs.detectChanges();
  }

  removeSelectedPosSetting(): void {
    if (!this.posTransactionSourceSelection.isEmpty()) {
      let paymentConfigUuid = this.getLeasePaymentConfigByUuid(this.selectedPaymentConfig!.uuid!)!.uuid!;
      lastValueFrom(this.leaseViewService.removePaymentConfigFromLease(paymentConfigUuid, this.id))
        .then(() => {
          this.reloadLease.emit();
          this.loadLeasePaymentConfigHistory();
        })
        .catch((error: string) => {
          console.log(error);
        })
    }
  }

  cardReadersOverlap(): boolean {
    let overlap = false;
    const allReaders = this.cardReaders.map(m => m);
    const newCardReaders = this.newCardReaders.filter(f => f.cardReaderCode != null);
    newCardReaders.length > 0 ? allReaders.push(...newCardReaders) : null;
    allReaders.forEach((reader1, i) => {
      allReaders.forEach((reader2, j) => {
        if (i != j) {
          overlap = overlap || (new Date(reader1.startDate!) <= new Date(reader2.endDate!) && new Date(reader1.endDate!) >= new Date(reader2.startDate!))
        }
      })
    })
    return overlap;
  }

  get displayTransactionSourceTable() {
    return this.leaseTransactionSourceConfigDataSource
      && this.leaseTransactionSourceConfigDataSource.data
      && this.leaseTransactionSourceConfigDataSource.data.length > 0
  }

  get displaySupplierSourceTable() {
    return this.leaseSupplierSourceConfigDataSource
      && this.leaseSupplierSourceConfigDataSource.data
      && this.leaseSupplierSourceConfigDataSource.data.length > 0
  }

  getLeasePricingPeriodRequiredMessage(): string {
    return 'Cannot add a lease payment config without Rates and Charges being completed';
  }

  getLeaseMustBeApprovedRequiredMessage(): string {
    return 'Lease must be approved before adding a lease payment config';
  }

  getCardReaderLeaseNotActiveMessage(): string {
    return 'Cannot add/edit card readers on a planned/expired/terminated lease';
  }

  getMaskedAccessToken(accessToken: string): string {
    if (this.isInternalUser) {
      return accessToken;
    }
    return '**********'
  }

  get hasOwnPosSettings() {
    return this.paymentConfigs.filter(f => f.transactionSource == "VEND").length > 0;
  }

  get isSubLease() {
    return this.leaseSummary.agreementType == AgreementType.SUB_LEASE;
  }

  get isHeadLease() {
    return this.leaseSummary.agreementType == AgreementType.LEASE && this.leaseSummary.occupancyType == OccupancyType.SHARED;
  }

  get allowedPosTypes() {
    if (this.isSubLease) return [TransactionSource.VEND_SUPPLIER_LEASE];
    if (this.isHeadLease) return [TransactionSource.VEND];
    return [TransactionSource.VEND, TransactionSource.VEND_SUPPLIER_OFFERING, TransactionSource.POSTER, TransactionSource.NETCASH];
  }

  getProviderName(provider: string) {
    if (provider == 'VEND') return 'Lightspeed Retail X (Vend)';
    return provider;
  }

  private loadLeasePaymentConfigHistory(): void {
    lastValueFrom(this.leaseViewService.getLeasePaymentConfigHistory(this.id))
      .then((res: PaymentConfigRequest[]) => {
        this.paymentConfigs = mapRequestsToData(res);
        this.loadLeasePaymentConfigHistoryToTable(this.paymentConfigs);
        this.posSupplierSourceSelection.clear();
        this.posTransactionSourceSelection.clear();
      })
  }

  protected readonly OccupancyType = OccupancyType;
  protected readonly PanelType = PanelType;

  private getLeasePaymentConfigByUuid(uuid: string): PaymentConfigData | null {
    let index: number = this.paymentConfigs.findIndex((val) => val.uuid == uuid);
    if (index >= 0) {
      return this.paymentConfigs.at(index)!;
    } else {
      return null;
    }
  }

  getLeasePaymentConfigParentByUuid(uuid: string): PaymentConfigData | null {
    let index: number = this.parentPaymentConfigs.findIndex((val) => val.uuid == uuid);
    if (index >= 0) {
      return this.paymentConfigs.at(index)!;
    } else {
      return null;
    }
  }
}
